import type { Integration } from '@sentry/core';
import { isPlainObject } from '@sentry/core';

/*
 * This is based on https://github.com/getsentry/sentry-javascript/pull/2422, which shortly
 * added a sanitizer before being removed because all sanitization rules for all SDKs were being
 * consolidated in a new backend feature that we don't have yet.
 */

const sanitizeMask = '******';

export interface DataScrubberOptions {
  sanitizeKeys: Array<string | RegExp>;
}

/**
 * Removes any entries from `input` that match `regex`.
 *
 * The `visited` set is to avoid infinite recursion when fields map to parent fields.
 */
function sanitize<T extends unknown | Record<string, unknown> | Record<string, unknown>[]>(
  input: T,
  regex: RegExp,
  /** Visited set to avoid infinite recurisions where fields map to parent fields. */
  visited: WeakSet<object>
): T {
  const inputIsArray = Array.isArray(input);
  const inputIsPlainObject = isPlainObject(input);

  if (!inputIsArray && !inputIsPlainObject) {
    return input;
  }

  if (visited.has(input)) {
    return input;
  }

  visited.add(input);

  if (inputIsArray && Array.isArray(input)) {
    return input.map(value => sanitize(value, regex, visited)) as T;
  }

  if (inputIsPlainObject) {
    const inputVal = input as Record<string, unknown>;
    return Object.keys(inputVal).reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = regex.test(key) ? sanitizeMask : sanitize(inputVal[key], regex, visited);
      return acc;
    }, {}) as T;
  }

  return input;
}

/** Joins regexes into one regex. */
function combineRegexes(input: (string | RegExp)[]): RegExp {
  const regexes = input.map(key => {
    if (typeof key === 'string') {
      // escape string value
      // see also: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#Escaping
      return key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
    return key.source;
  });
  return RegExp(regexes.join('|'), 'i');
}

export const createDataScrubberIntegration = (options: DataScrubberOptions): Integration => {
  let regex: RegExp | undefined;

  return {
    name: 'DataScrubberIntegration',
    processEvent(event, _hint, _client) {
      if (options.sanitizeKeys.length === 0) {
        return event;
      }
      if (!regex) {
        regex = combineRegexes(options.sanitizeKeys);
      }
      return sanitize(event, regex, new WeakSet());
    },
  };
};
